import React, { useState, useRef, useLayoutEffect } from 'react';
import { toast } from 'react-hot-toast';
import { traitCollections } from '../config/stepsConfig';
import StepContainer from './StepContainer';  
import logo from '../images/logo.png';

function NavigationDrawer({
  stepsData,
  onStepChange,
  currentStep,
  selectedOptions,
  activeCollection,
  isCollectionOpen,
  onCollectionChange,
  onOptionSelect,
  currentStepData,
  totalSteps,
  collectionId,
  handlePreviousStep,
  handleNextStep
}) {
  const [isStepContainerOpen, setIsStepContainerOpen] = useState(false);
  const stepContainerRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Helper function to check if a step should be disabled
  const isStepDisabled = (index) => {
    // First step (background) is always enabled
    if (index === 0) return false;
    
    // Second step (body) is always enabled
    // if (index === 1) return false;
    
    // For all other steps, require background and body selections
    const hasBackground = selectedOptions.background;
    // const hasBody = selectedOptions.body;
    
    return !hasBackground;
  };

  // Handle button click with validation
  const handleStepClick = (index) => {
    if (isStepDisabled(index)) {
      toast.error('Please complete Background selections first!', {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#FF4B4B',
          color: '#fff',
          borderRadius: '10px',
          padding: '16px',
        }
      });
      return;
    }
    onStepChange(index);
    setIsStepContainerOpen(true);
  };

  // Handle close button click
  const handleCloseStepContainer = () => {
    setIsStepContainerOpen(false);
  };

  // Preserve scroll position when options change
  const handleOptionSelect = (stepName, option) => {
    // Save scroll position before state change - use the scrollable content area
    if (scrollableContentRef.current) {
      scrollPositionRef.current = scrollableContentRef.current.scrollTop;
    }
    onOptionSelect(stepName, option);
  };

  // Restore scroll position after re-render
  useLayoutEffect(() => {
    if (scrollableContentRef.current && scrollPositionRef.current > 0) {
      // Use multiple requestAnimationFrame calls to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollableContentRef.current) {
            scrollableContentRef.current.scrollTop = scrollPositionRef.current;
          }
        });
      });
    }
  });

  // Reset scroll position when step changes
  useLayoutEffect(() => {
    if (scrollableContentRef.current) {
      scrollPositionRef.current = 0;
      scrollableContentRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  // Navigation Section Component
  const NavigationSection = () => (
    <div className="min-w-0 bg-black">
      {/* Collections */}
      <div className="flex-grow-0 h-full flex flex-col justify-between">
        {traitCollections.map(collection => (
          <div key={collection.id}>
            {collection.id === activeCollection && (
              <div className="bg-black">
                {stepsData.map((step, index) => (
                  <button
                    key={step.stepName}
                    className={`w-full text-left px-2 py-2 font-vag text-fluid-sm font-normal border-b border-[#554D82] hover:bg-[#F8F7FF] hover:text-black transition-all duration-300 md:px-5 md:py-5 md:text-fluid-md lg:text-fluid-lg ${
                      currentStep - 1 === index && isStepContainerOpen ? 'bg-[#F8F7FF] text-black' : 'text-white'
                    } ${
                      isStepDisabled(index) ? 'opacity-50 cursor-not-allowed bg-gray-800 hover:bg-gray-800 hover:text-white' : ''
                    }`}
                    disabled={isStepDisabled(index)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStepClick(index);
                    }}
                  >
                    {step.title}
                  </button>
                ))}
              </div>
            )}  
          </div>
        ))}
      </div>
    </div>
  );

  // Step Container Section
  const StepContainerSection = () => (
    <>
    <div ref={stepContainerRef} className={`fixed top-0 left-[115px] md:left-[150px] h-screen w-1/2 bg-[#F8F7FF] overflow-auto flex flex-col transition-all duration-300 ease-in-out z-50 lg:left-[200px] lg:w-1/3 ${
      isStepContainerOpen ? 'translate-x-0 opacity-100 pointer-events-auto' : '-translate-x-96 opacity-0 pointer-events-none'
    }`}>
      {/* Header with close button */}
      <div className="flex justify-between items-center p-6 pb-2">
        <h2 className="text-black text-fluid-lg font-bold font-vag">
          Select {currentStepData?.title || 'Option'}
        </h2>
        <button
          onClick={handleCloseStepContainer}
          className="flex items-center gap-2 text-black hover:text-purple-200 transition-colors duration-200"
        >
          <span className="text-lg">×</span>
          <span className="text-sm">Close</span>
        </button>
      </div>
      
      {/* Content */}
      <div ref={scrollableContentRef} className="flex-1 p-6 overflow-auto">
        <StepContainer
          currentStep={currentStep}
          selectedOptions={selectedOptions}
          onOptionSelect={handleOptionSelect}
          stepsData={stepsData}
          currentStepData={currentStepData}
          handleStepChange={onStepChange}
          totalSteps={totalSteps}
          collectionId={collectionId}
          handlePreviousStep={handlePreviousStep}
          handleNextStep={handleNextStep}
        />
      </div>

    </div>
    {/* Overlay */}
    {isStepContainerOpen && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-30"
        onClick={handleCloseStepContainer}
      />
    )}
    </>
  );

  return (
    <>      
      <div className="w-[115px] md:w-[150px] lg:w-[200px] bg-black z-50">
        {/* LOGO AREA */}
        <div className="flex items-center justify-center flex-col gap-2 px-4 py-3">
          <img src={logo} alt="logo" className="w-16 h-10 md:w-[100px] md:h-[60px]" />
          <span className="text-white text-fluid-lg md:text-fluid-2xl font-bold font-vag">
            MyToon
          </span>
        </div>
        {/* NAVIGATION SECTION */}
        <div className="lg:flex bg-black z-30">
          <div
            className="flex flex-row w-full"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <NavigationSection />
          </div>
          {/* <Footer /> */}
        </div>
      </div>
      <StepContainerSection />
    </>
  );
}

export default NavigationDrawer;
